import { useEffect, useRef } from 'react';

export function Modal({ open, onClose, title, subtitle, children, footer, fixedActions = false, sidePanel = false }) {
  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    if (!el) return;

    const previousFocus = document.activeElement;
    const getFocusable = () => [...el.querySelectorAll(
      'button:not(:disabled), [href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    )].filter(node => !node.hidden);
    const timer = setTimeout(() => (getFocusable()[0] || el).focus(), 50);

    function handleKeyDown(e) {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key === 'Tab') {
        const focusable = getFocusable();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) { e.preventDefault(); el.focus(); return; }
        if (e.shiftKey) {
          if (document.activeElement === first || !el.contains(document.activeElement)) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onCloseRef.current(); }}
    >
      <div
        ref={modalRef}
        className={`modal${fixedActions ? ' modal-fixed-actions' : ''}${sidePanel ? ' modal-side-panel' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={fixedActions ? undefined : { maxHeight: '90vh', overflowY: 'auto' }}
      >
        {sidePanel && !fixedActions && <button type="button" className="modal-close modal-side-close" aria-label="Close dialog" onClick={() => onCloseRef.current()}>×</button>}
        {fixedActions ? <>
          <div className="modal-fixed-header">
            <div>
              {title && <div className="modal-title">{title}</div>}
              {subtitle && <div className="modal-sub">{subtitle}</div>}
            </div>
            <button type="button" className="modal-close" aria-label="Close dialog" onClick={() => onCloseRef.current()}>×</button>
          </div>
          <div className="modal-scroll-body">{children}</div>
          {footer && <div className="modal-fixed-footer">{footer}</div>}
        </> : <>
          {title && <div className="modal-title">{title}</div>}
          {subtitle && <div className="modal-sub">{subtitle}</div>}
          {children}
          {footer && <div className="modal-footer">{footer}</div>}
        </>}
      </div>
    </div>
  );
}
